import React, { useState } from 'react';
import { ChevronRight, Droplet, Leaf, Zap, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export const FlavorMenu = () => {
  const flavorCategories = [
    {
      id: 'citrus',
      name: 'Citrus',
      icon: Zap,
      color: 'from-orange-400 to-yellow-400',
      borderColor: 'border-orange-400/30',
      iconColor: 'text-orange-400',
      bgColor: 'bg-orange-400/10',
      description: 'Bright, energizing citrus blends to kickstart your day',
      products: [
        { name: 'Citrus Sunrise', benefits: 'High Vitamin C, Natural Energy', ingredients: 'Orange, Lemon, Grapefruit' },
        { name: 'Tangerine Dream', benefits: 'Immune Support, Refreshing', ingredients: 'Tangerine, Lime, Mint' }
      ]
    },
    {
      id: 'greens',
      name: 'Greens',
      icon: Leaf,
      color: 'from-lime-400 to-green-500',
      borderColor: 'border-lime-400/30',
      iconColor: 'text-lime-400',
      bgColor: 'bg-lime-400/10',
      description: 'Nutrient-packed green juices for vitality and wellness',
      products: [
        { name: 'Green Power', benefits: 'Vitamin Boost, Detox Support', ingredients: 'Kale, Spinach, Apple, Ginger' },
        { name: 'Emerald Energy', benefits: 'Alkalizing, Energy', ingredients: 'Cucumber, Celery, Lemon' }
      ]
    },
    {
      id: 'roots',
      name: 'Roots & Berries',
      icon: Droplet,
      color: 'from-pink-400 to-red-500',
      borderColor: 'border-pink-400/30',
      iconColor: 'text-pink-400',
      bgColor: 'bg-pink-400/10',
      description: 'Earthy roots and vibrant berries for deep nutrition',
      products: [
        { name: 'Berry Blast', benefits: 'Antioxidants, Heart Health', ingredients: 'Beets, Strawberries, Blueberries, Carrot, Apple' },
        { name: 'Ginger Root Power', benefits: 'Anti-inflammatory, Digestive', ingredients: 'Ginger, Turmeric, Carrot, Orange, Lemon' }
      ]
    },
    {
      id: 'specialty',
      name: 'Specialty Mixes',
      icon: Sparkles,
      color: 'from-purple-400 to-indigo-500',
      borderColor: 'border-purple-400/30',
      iconColor: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
      description: 'Unique blends for specific wellness goals',
      products: [
        { name: 'Midnight Orchard', benefits: 'Antioxidants, Sleep Support', ingredients: 'Dark Berries, Cherry, Lavender' },
        { name: 'Tropical Tangy', benefits: 'Immune Boost, Exotic Flavors', ingredients: 'Mango, Passion Fruit, Pineapple' }
      ]
    },
    {
      id: 'shots',
      name: 'Shot Packs',
      icon: Zap,
      color: 'from-amber-400 to-orange-500',
      borderColor: 'border-amber-400/30',
      iconColor: 'text-amber-400',
      bgColor: 'bg-amber-400/10',
      description: 'Concentrated wellness shots for quick energy boosts',
      products: [
        { name: 'Rising Lion', benefits: 'Energy, Focus, Alertness', ingredients: 'Ginger, Turmeric, Cayenne' },
        { name: 'Immunity Shield', benefits: 'Immune Support, Wellness', ingredients: 'Elderberry, Echinacea, Vitamin C' }
      ]
    }
  ];

  return (
    <section className="py-24 bg-black">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/20 mb-4">
            <Droplet className="w-3 h-3 mr-1" />
            Flavor Menu
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Explore Our <span className="text-amber-400">Flavors</span>
          </h2>
          <p className="text-xl text-gray-300">
            Color-coded by category. Click to explore full nutritional details,
            benefits, and lifestyle pairings.
          </p>
        </div>

        {/* Flavor Categories Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-12">
          {flavorCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card
                key={category.id}
                className={`bg-zinc-900 border ${category.borderColor} hover:border-opacity-100 transition-all group cursor-pointer`}
              >
                <CardContent className="pt-6 pb-6 text-center">
                  <div className={`${category.bgColor} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-8 h-8 ${category.iconColor}`} />
                  </div>
                  <h3 className={`text-lg font-bold text-white mb-1 group-hover:${category.iconColor} transition-colors`}>
                    {category.name}
                  </h3>
                  <p className="text-xs text-gray-400 line-clamp-2">
                    {category.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Expandable Details */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="pt-8">
            <Accordion type="single" collapsible className="w-full">
              {flavorCategories.map((category) => {
                const Icon = category.icon;
                return (
                  <AccordionItem key={category.id} value={category.id} className="border-zinc-800">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center space-x-3">
                        <div className={`${category.bgColor} p-2 rounded-lg`}>
                          <Icon className={`w-5 h-5 ${category.iconColor}`} />
                        </div>
                        <div className="text-left">
                          <p className="text-white font-bold">{category.name}</p>
                          <p className="text-sm text-gray-400">{category.description}</p>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-4 pt-4">
                        {category.products.map((product, index) => (
                          <div
                            key={index}
                            className="bg-zinc-800 rounded-lg p-4 hover:bg-zinc-700 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="text-white font-semibold">{product.name}</h4>
                              <Badge className={`${category.bgColor} ${category.iconColor} border-0`}>
                                $3.99
                              </Badge>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div>
                                <span className="text-gray-400">Benefits: </span>
                                <span className="text-gray-300">{product.benefits}</span>
                              </div>
                              <div>
                                <span className="text-gray-400">Ingredients: </span>
                                <span className="text-gray-300">{product.ingredients}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">
            All juices are cold-pressed, never heated, and made fresh to order
          </p>
          <Badge className="bg-lime-400/10 text-lime-400 border-lime-400/20">
            100% Natural • No Added Sugar • Gluten-Free • Vegan
          </Badge>
        </div>
      </div>
    </section>
  );
};
